import { useEffect, useState } from "react";
import { Button, ButtonGroup } from "reactstrap";
import Dropdown from "../../../../components/DropDown/DropDown";
import { ISelectedFilters } from "../../../../types";
import useFilters from "../../Hooks/useFilters";
import moment from "moment";
import DateRange from "../../../../components/DateRange/DateRange";
import { State } from "../../../../Reducers/types";
import { useSelector } from "react-redux";

type Props = {
  onSearch: (selected: ISelectedFilters) => void;
  onClear: () => void;
  onFilterChange?: (selected: ISelectedFilters) => void;
  hasDistrictFilter?: boolean;
  hasBlockFilter?: boolean;
  hasSchoolFilter?: boolean;
  hasGradeFilter?: boolean;
  hasDateFilter?: boolean;
  hasWeekMonthToggle?: boolean;
  hasDayWeekToggle?: boolean;
  hasMonthSelector?: boolean;
  hasWeekSelector?: boolean;
  onExportToExcel?: () => void;
  userRole?: number;
};

const Filter = ({
  onSearch,
  onClear,
  onFilterChange,
  hasDistrictFilter = false,
  hasBlockFilter = false,
  hasSchoolFilter = false,
  hasGradeFilter = false,
  hasDateFilter = false,
  hasWeekMonthToggle = false,
  hasDayWeekToggle = false,
  hasMonthSelector = false,
  hasWeekSelector = false,
  onExportToExcel,
  userRole,
}: Props) => {
  const { district, school, state, block } = useSelector(
    (state: State) => state.user.userProfile
  );
  const {
    handleSelect,
    selectedFilters,
    getDistrictList,
    districtList,
    getBlockList,
    blockList,
    getSchoolList,
    schoolList,
    getGradeList,
    gradeList,
    resetSelectedFilters,
  } = useFilters(hasWeekMonthToggle, hasDayWeekToggle);

  // get grades and district list on load
  useEffect(() => {
    if (hasGradeFilter) {
      getGradeList();
    }
  }, [hasGradeFilter]);
  useEffect(() => {
    if (hasDistrictFilter) {
      if (userRole !== 7) {
        getDistrictList();
      } else if (hasBlockFilter) {
        getBlockList(district.id);
      }
    }
    if (hasBlockFilter && userRole === 6) {
      //only runs for block officer
      getBlockList(district.id);
    }
  }, [hasDistrictFilter, hasBlockFilter, userRole]);
  const [selectedMonth, setSelectedMonth] = useState<number | undefined>();
  const [selectedWeek, setSelectedWeek] = useState<number | undefined>();

  const updateDateFromSelector = (type: "week" | "month", value: number) => {
    const currentYear = moment().year();
    let month = "01";
    let date = { start: "01", end: "31" };
    if (type === "week") {
      setSelectedWeek(value);
      month = `${selectedMonth < 10 ? "0" : ""}${selectedMonth}`;
      const dates = {
        1: { start: "01", end: "07" },
        2: { start: "08", end: "14" },
        3: { start: "15", end: "21" },
        4: {
          start: "22",
          end: moment(`${currentYear}-${month}-01`).endOf("month").date(),
        },
      };
      date = dates[selectedMonth];
    }
    if (type === "month") {
      setSelectedMonth(value);
      month = `${value < 10 ? "0" : ""}${value}`;
      date = {
        start: "01",
        end: "" + moment(`${currentYear}-${value}-01`).endOf("month").date(),
      };
    }
    handleSelect(
      {
        start_date: `${currentYear}-${month}-${date?.start}`,
        end_date: `${currentYear}-${month}-${date?.end}`,
      },
      "date"
    );
  };
  useEffect(() => {
    if (selectedFilters && onFilterChange) {
      onFilterChange({
        districtId: selectedFilters?.district?.id,
        blockId: selectedFilters?.block?.id,
        schoolId: selectedFilters?.school?.id,
        gradeId: selectedFilters?.grade?.id,
        date: selectedFilters?.date,
      });
    }
  }, [selectedFilters]);

  const [isWeek, setIsWeek] = useState<boolean>(true);
  return (
    <div className="filter-div-bg p-2 mt-3">
      <div>
        <span>Filter: &nbsp;&nbsp;&nbsp;</span>
        {userRole === 7 ? (
          <span className="px-3 small">{`District: ${district.name}`}</span>
        ) : (
          hasDistrictFilter &&
          userRole !== 6 && (
            <Dropdown
              selectedValue={selectedFilters?.district?.id}
              options={districtList.map((d) => ({ id: d?.id, name: d?.name }))}
              onChange={(value) => {
                handleSelect(value, "district");
                if (hasBlockFilter) {
                  getBlockList(value?.id);
                }
              }}
              type="District"
            />
          )
        )}
        {userRole === 6 ? (
          <span className="px-3 small">{`Block: ${block.name}`}</span>
        ) : (
          hasBlockFilter && (
            <Dropdown
              selectedValue={selectedFilters?.block?.id}
              options={blockList.map((d) => ({ id: d?.id, name: d?.name }))}
              onChange={(value) => {
                handleSelect(value, "block");
                if (hasSchoolFilter) {
                  getSchoolList(value?.id);
                }
              }}
              type="Block"
            />
          )
        )}
        {hasSchoolFilter && (
          <Dropdown
            selectedValue={selectedFilters?.school?.id}
            options={schoolList.map((d) => ({ id: d?.id, name: d?.name }))}
            onChange={(value) => {
              handleSelect(value, "school");
            }}
            type="School"
          />
        )}
        {hasGradeFilter && (
          <Dropdown
            selectedValue={selectedFilters?.grade?.id}
            options={gradeList?.filter((g => g.id !== 5))?.map((d) => ({ id: d?.id, name: d?.name }))}
            onChange={(value) => {
              handleSelect(value, "grade");
            }}
            type="Grade"
          />
        )}
        {hasDateFilter && (
          <DateRange
            // hasTimeFilter
            onSubmit={(value) => {
              handleSelect(value, "date");
            }}
            initialDate={{
              start_date: moment().format("YYYY-MM-DD"),
              end_date: moment().format("YYYY-MM-DD"),
            }}
            onCancel={() => {
              handleSelect(undefined, "date");
            }}
          />
        )}
        {(hasWeekMonthToggle || hasDayWeekToggle) && (
          <ButtonGroup>
            <Button
              color={isWeek ? "primary" : "secondary"}
              size="sm"
              onClick={() => {
                setIsWeek(true);
                handleSelect(
                  {
                    start_date: moment()
                      .subtract(hasWeekMonthToggle ? 7 : 0, "days")
                      .format("YYYY-MM-DD"),
                    end_date: moment().add(1, "days").format("YYYY-MM-DD"),
                  },
                  "date"
                );
              }}
            >
              {hasWeekMonthToggle ? "Week" : "Today"}
            </Button>
            <Button
              color={!isWeek ? "primary" : "secondary"}
              size="sm"
              onClick={() => {
                setIsWeek(false);
                handleSelect(
                  {
                    start_date: moment()
                      .subtract(hasWeekMonthToggle ? 30 : 7, "days")
                      .format("YYYY-MM-DD"),
                    end_date: moment().add(1, "days").format("YYYY-MM-DD"),
                  },
                  "date"
                );
              }}
            >
              {hasWeekMonthToggle ? "Month" : "This Week"}
            </Button>
          </ButtonGroup>
        )}

        {hasMonthSelector && (
          <Dropdown
            selectedValue={selectedMonth}
            options={[
              { name: "Jan", id: 1 },
              { name: "Feb", id: 2 },
              { name: "Mar", id: 3 },
              { name: "Apr", id: 4 },
              { name: "May", id: 5 },
              { name: "Jun", id: 6 },
              { name: "Jul", id: 7 },
              { name: "Aug", id: 8 },
              { name: "Sep", id: 9 },
              { name: "Oct", id: 10 },
              { name: "Nov", id: 11 },
              { name: "Dec", id: 12 },
            ].map((d) => ({ id: d.id, name: d.name }))}
            onChange={(value) => {
              updateDateFromSelector("month", value.id);
            }}
            type="Month"
          />
        )}
        {hasWeekSelector && (
          <Dropdown
            isDisabled={selectedMonth === undefined}
            selectedValue={selectedWeek}
            options={[
              { name: "1st Week (1-7)", id: 1 },
              { name: "2nd Week (8-14)", id: 2 },
              { name: "3rd Week (15-21)", id: 3 },
              { name: "4th Week (22-end of month)", id: 4 },
            ].map((d) => ({ id: d.id, name: d.name }))}
            onChange={(value) => {
              updateDateFromSelector("week", value.id);
            }}
            type="Week"
          />
        )}
      </div>
      <div className="">
        {selectedFilters && (
          <Button
            size="sm"
            color="dark"
            className="ml-2"
            onClick={() => {
              resetSelectedFilters();
              setIsWeek(true);
              setSelectedMonth(-1);
              setSelectedWeek(-1);
              onClear();
            }}
          >
            Clear <i className="fas fa-times"></i>
          </Button>
        )}
        {onSearch ? (
          <Button
            size="sm"
            color="primary"
            onClick={() =>
              onSearch({
                districtId: selectedFilters?.district?.id,
                blockId: selectedFilters?.block?.id,
                schoolId: selectedFilters?.school?.id,
                gradeId: selectedFilters?.grade?.id,
                date: selectedFilters?.date,
              })
            }
          >
            Search
          </Button>
        ) : (
          <></>
        )}
        {selectedFilters && onExportToExcel && (
          <Button
            size="sm"
            color="success"
            className="ml-2"
            onClick={onExportToExcel}
          >
            Export to excel <i className="fas fa-chart"></i>
          </Button>
        )}
      </div>
    </div>
  );
};

export default Filter;
